import {Button, InputField, Textarea} from '@nlabs/gothamjs/components';
import {ArrowRight, Film, Sparkles, Video} from '@nlabs/gothamjs/icons';
import {estimateGenerationCredits, formatCredits} from '@reactorbox/shared';
import {useRef, useState} from 'react';

import {getOneOffVideoResults, getPendingOneOffVideo} from '../../state/workspaceSelectors.js';
import {pageHeading, secondaryButton} from '../../theme/classes.js';
import {createRequestId} from '../../utils/createRequestId.js';
import {PreviewFrame} from '../PreviewFrame/PreviewFrame.js';
import {VideoResultCard} from '../VideoResultCard/VideoResultCard.js';

import type {OneOffVideoRequest, Resolution, Workspace} from '@reactorbox/shared';
import type {FormEvent, ReactElement} from 'react';
import type {WorkspaceActions} from '../../state/workspaceActions.js';

interface OneOffVideoProps {
  readonly actions: WorkspaceActions;
  readonly busy: boolean;
  readonly onCreateProject: () => void;
  readonly workspace: Workspace;
}

const composerLabel = 'mb-2 block text-xs font-semibold tracking-wide text-[#28212f]/70';
const composerInput = 'w-full! min-w-0! rounded-2xl! border! border-white/35! bg-white/20! px-4! py-3! text-base! leading-6! text-[#241e2c]! shadow-none! outline-none! placeholder:text-[#241e2c]/40! focus:border-white/80! focus:ring-2! focus:ring-white/45! sm:text-sm!';
const composerButton = 'inline-flex! min-h-13! w-full! items-center! justify-center! rounded-full! border-0! bg-violet-600! px-6! py-3.5! text-sm! font-semibold! text-white! shadow-none! transition hover:bg-violet-700! focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-violet-700 disabled:cursor-not-allowed! disabled:opacity-40!';
const composerNotice = 'rounded-2xl border border-[#6d4532]/15 bg-white/25 px-4 py-3 text-xs leading-relaxed text-[#70412e]';

export const OneOffVideo = ({actions, busy, onCreateProject, workspace}: OneOffVideoProps): ReactElement => {
  const [prompt, setPrompt] = useState('');
  const [title, setTitle] = useState('');
  const [duration, setDuration] = useState(5);
  const [resolution, setResolution] = useState<Resolution>('720p');
  const [referenceUrl, setReferenceUrl] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  const validDuration = Number.isInteger(duration) && duration >= 4 && duration <= 30;
  const price = validDuration ? estimateGenerationCredits('video', duration, workspace.pricing) : undefined;
  const affordable = price !== undefined && workspace.credits.available >= price;
  const validReference = !referenceUrl.trim() || /^https:\/\/.+/i.test(referenceUrl.trim());
  const liveReference = workspace.mode === 'demo' || Boolean(referenceUrl.trim());
  const activeResult = getPendingOneOffVideo(workspace, {duration, prompt, referenceUrl, resolution, title});
  const ready = !busy && !activeResult && prompt.trim().length > 0 &&
    validDuration && affordable && validReference && liveReference;
  const results = getOneOffVideoResults(workspace);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if(!ready) {
      return;
    }
    void actions.mutate('/api/videos', {
      duration,
      idempotencyKey: createRequestId(),
      prompt: prompt.trim(),
      referenceUrl: referenceUrl.trim() || undefined,
      resolution,
      title: title.trim() || undefined
    }, workspace.mode === 'demo' ? 'Quick video preview queued.' : 'Quick video generation queued.');
  };

  const reuse = (settings: Omit<OneOffVideoRequest, 'idempotencyKey'>): void => {
    setDuration(settings.duration);
    setPrompt(settings.prompt);
    setReferenceUrl(settings.referenceUrl || '');
    setResolution(settings.resolution);
    setTitle(settings.title || '');
    formRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
    formRef.current?.querySelector<HTMLTextAreaElement>('textarea')?.focus({preventScroll: true});
  };

  return (
    <div className="mx-auto w-full max-w-[1540px] pb-16 pt-4 sm:pt-6 lg:pt-8">
      <header className="mb-4 flex items-center justify-between gap-3 sm:mb-8 sm:items-end">
        <div className="min-w-0">
          <p className="mb-3 hidden text-[11px] font-medium tracking-[0.18em] text-white/50 uppercase sm:block">Quick video</p>
          <h1 className={`${pageHeading} text-2xl! sm:text-4xl!`}>One idea. One video<span className="text-violet-200">.</span></h1>
        </div>
        <span className="inline-flex shrink-0 items-center gap-2 rounded-full border border-white/10 bg-white/5 px-2.5 py-2 text-[10px] text-white/65 sm:px-3 sm:text-[11px]">
          <span className="size-1.5 rounded-full bg-violet-200" />
          {workspace.mode === 'demo' ? 'Local preview' : 'Live generation'}
        </span>
      </header>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.12fr)_minmax(360px,0.88fr)] xl:gap-6">
        <div className="order-last flex min-w-0 flex-col lg:order-first">
          <figure className="relative h-60 min-h-60 flex-none overflow-hidden rounded-[2rem] border border-white/10 bg-[#151419] sm:h-80 sm:min-h-80 lg:h-auto lg:min-h-[640px] lg:flex-1">
            <div className="absolute inset-0 [&>div]:h-full [&>div]:w-full">
              <PreviewFrame index={2} label="Illustrative cinematic artwork, not a generated video" />
            </div>
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-linear-to-t from-black/75 via-transparent to-black/15" />
            <div className="absolute inset-x-5 top-5 flex flex-wrap items-center justify-between gap-2 sm:inset-x-7 sm:top-7">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/25 px-3 py-2 text-[10px] font-medium tracking-widest text-white/90 backdrop-blur-xl">
                <Film size={12} /> MOTION STUDY
              </span>
              <span className="rounded-full bg-black/25 px-3 py-2 text-[10px] text-white/75 backdrop-blur-xl">Illustrative artwork</span>
            </div>
            <figcaption className="absolute inset-x-6 bottom-7 sm:inset-x-9 sm:bottom-10">
              <p className="mb-2 text-[10px] font-medium tracking-[0.2em] text-white/55 uppercase">A little imagination. A little motion.</p>
              <p className="max-w-sm text-3xl leading-[1.08] font-normal tracking-tight text-white sm:text-4xl xl:text-5xl">See where<br />the idea takes you.</p>
            </figcaption>
          </figure>
          <div className="flex flex-wrap items-center justify-between gap-3 px-1 pt-4 text-xs text-white/50">
            <span>For moments, experiments, and everything between.</span>
            <Button className="inline-flex! min-h-8! gap-2! rounded-full! border-0! bg-transparent! p-0! text-xs! font-medium! text-sky-200! hover:text-sky-100!" onClick={onCreateProject} variant="text">
              Start a story project <ArrowRight size={13} />
            </Button>
          </div>
        </div>

        <form className="relative isolate order-first flex min-w-0 flex-col gap-4 overflow-hidden rounded-[2rem] border border-white/30 bg-linear-to-br from-[#d9cff8]/95 via-[#b9b6ee]/95 to-[#72c9ed]/95 p-5 text-[#241e2c] shadow-[0_20px_90px_-50px_#a694c0] backdrop-blur-3xl sm:gap-5 sm:p-7 lg:order-last lg:p-8" onSubmit={submit} ref={formRef}>
          <div aria-hidden="true" className="pointer-events-none absolute -top-20 -right-12 -z-10 size-64 rounded-full bg-white/40 blur-3xl" />
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-xl font-medium tracking-tight text-violet-950">Create a video</h2>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/40 bg-white/20 px-2.5 py-1.5 text-[10px] font-medium"><Sparkles size={11} />{workspace.mode === 'demo' ? 'Preview mode' : 'Seedance 2.5'}</span>
          </div>

          <div>
            <label className={composerLabel} htmlFor="video-prompt">Video prompt</label>
            <Textarea
              className={`${composerInput} min-h-36! resize-y! rounded-[1.35rem]! leading-relaxed! sm:min-h-40! sm:text-[15px]!`}
              id="video-prompt"
              maxLength={4000}
              onChange={(event) => setPrompt(event.target.value)}
              placeholder="A quiet city at dawn. The camera drifts past a coffee shop, warm light spilling onto the empty street…"
              required
              rows={5}
              value={prompt}
            />
            <p className="mt-2 text-[11px] text-[#241e2c]/50">Your subject, the movement, the mood.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={composerLabel} htmlFor="video-duration">Duration (seconds)</label>
              <input
                aria-label="Video duration (seconds)"
                className={`${composerInput} rounded-full!`}
                id="video-duration"
                max={30}
                min={4}
                onChange={(event) => setDuration(Number(event.target.value))}
                required
                step={1}
                type="number"
                value={duration}
              />
            </div>
            <div>
              <label className={composerLabel} htmlFor="video-resolution">Resolution</label>
              <select
                aria-label="Video resolution"
                className={`${composerInput} rounded-full!`}
                id="video-resolution"
                onChange={(event) => setResolution(event.target.value as Resolution)}
                value={resolution}
              >
                <option className="bg-[#e5d8eb] text-[#241e2c]">720p</option>
                <option className="bg-[#e5d8eb] text-[#241e2c]">480p</option>
              </select>
            </div>
          </div>

          <div>
            <label className={composerLabel} htmlFor="video-title">Video title <span className="font-normal opacity-60">/ optional</span></label>
            <InputField
              aria-label="Video title"
              borderType="none"
              className={composerInput}
              id="video-title"
              maxLength={100}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Give this moment a name"
              value={title}
            />
          </div>

          <div>
            <label className={composerLabel} htmlFor="video-reference">
              Reference image URL <span className="font-normal opacity-60">/ {workspace.mode === 'demo' ? 'optional' : 'required for this model'}</span>
            </label>
            <InputField
              aria-label="Video reference URL"
              borderType="none"
              className={composerInput}
              id="video-reference"
              onChange={(event) => setReferenceUrl(event.target.value)}
              placeholder="https://…"
              required={workspace.mode === 'live'}
              type="url"
              value={referenceUrl}
            />
            <p className="mt-2 text-[11px] leading-relaxed text-[#241e2c]/55">
              {workspace.mode === 'demo'
                ? 'Optional for previews. Live generation needs a public HTTPS image.'
                : 'A public HTTPS image becomes the first frame of your video.'}
            </p>
          </div>

          {!validReference && <p className={composerNotice}>Use an HTTPS URL for your reference image.</p>}
          {validDuration && !affordable && <p className={composerNotice}>
            Your available balance does not cover this clip. Try a shorter duration.
          </p>}
          {!validDuration && <p className={composerNotice}>Choose a whole-number duration from 4 to 30 seconds.</p>}
          {activeResult && <p className={composerNotice} role="status">
            {activeResult.job.status === 'reconciliation_required'
              ? 'This request is under review. Its credits remain reserved; you can change the settings for a different video.'
              : 'A video with these settings is already in progress. Follow it below, or change the settings for a new video.'}
          </p>}

          <div className="mt-auto border-t border-[#241e2c]/10 pt-5">
            <div className="mb-4 flex items-center justify-between gap-4">
              <div>
                <span className="block text-[10px] tracking-wide text-[#241e2c]/55">{workspace.mode === 'demo' ? 'Preview cost' : 'Generation cost'}</span>
                <strong className="text-2xl font-medium tracking-tight">{price === undefined ? '—' : formatCredits(price)}</strong>
              </div>
              <span className="max-w-40 text-right text-[11px] leading-relaxed text-[#241e2c]/55">{formatCredits(workspace.credits.available)} {workspace.mode === 'demo' ? 'test balance available' : 'developer allowance available'}</span>
            </div>
            <Button className={composerButton} disabled={!ready} icon={<Sparkles size={15} />} isLoading={busy} type="submit">
              {workspace.mode === 'demo' ? 'Generate preview video' : 'Generate video'}
            </Button>
            <p className="mt-3 text-center text-[10px] leading-relaxed text-[#241e2c]/55">
              {workspace.mode === 'demo'
                ? 'Simulated generation with test credits. No playable movie is created.'
                : 'Completed takes consume credits. Confirmed failures release the reservation.'}
            </p>
          </div>
        </form>
      </div>

      <section aria-labelledby="one-off-history-title" className="mt-14 sm:mt-20">
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <h2 className="flex items-center gap-3 text-2xl font-normal tracking-tight text-violet-200" id="one-off-history-title">Your quick videos <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs text-white/60">{results.length}</span></h2>
            <p className="mt-2 text-sm text-white/50">Every take, from the first idea to the one you keep.</p>
          </div>
        </div>
        {results.length > 0 ? (
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {results.map((result, index) => (
              <VideoResultCard index={index} key={result.job.id} onReuse={reuse} result={result} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center rounded-[2rem] border border-dashed border-white/15 bg-white/[0.025] px-6 py-12 text-center">
            <span className="mb-4 flex size-12 items-center justify-center rounded-2xl bg-white/5 text-white/50"><Video size={23} /></span>
            <h3 className="text-lg font-medium text-white/85">Your first clip starts above.</h3>
            <p className="mt-2 max-w-sm text-sm leading-relaxed text-white/50">Your video progress and finished takes will live here.</p>
            <Button className={`${secondaryButton} mt-5`} onClick={() => formRef.current?.querySelector<HTMLTextAreaElement>('textarea')?.focus()} variant="outline">Write your first prompt <ArrowRight size={13} /></Button>
          </div>
        )}
      </section>
    </div>
  );
};
